'use client';

import { Button } from '@ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/dialog';
import { Input } from '@ui/components/input';
import { Label } from '@ui/components/label';
import { AppCard, PageLayout } from '@ui/components/layouts';
import { SimpleSelect } from '@ui/components/select';
import { cn } from '@ui/lib/utils';
import { PageHeaderWithGardenSelector } from '@/src/components/PageHeaderWithGardenSelector';
import { PageContentLoading } from '@/src/components/PageContentLoading';
import { text as sharedText } from '@shared/text';
import { text } from '@modules/harvest/text';
import { toISODate } from '@shared/dates';
import type { MeasureUnit } from '@modules/harvest/business_logic';
import type { HarvestProps } from '@modules/harvest/containers/harvest_container';
import type { Id } from 'convex/_generated/dataModel';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faPenToSquare } from '@micks-yard/icons/solid/faPenToSquare';
import { faTrash } from '@micks-yard/icons/solid/faTrash';
import { useMemo } from 'react';

export function HarvestView(props: HarvestProps) {
  const today = toISODate(new Date());

  const plantOptions = useMemo(
    () =>
      props.gardenPlants.map((plant) => ({
        value: plant.id,
        label: `${plant.emoji} ${plant.name}`,
      })),
    [props.gardenPlants]
  );

  const formatDayHeader = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    });
  };

  const canQuickAdd =
    !!props.selectedGarden &&
    props.gardenPlants.length > 0 &&
    !!props.quickAddPlantId &&
    !!props.quickAddAmount.trim();

  if (props.isLoading) {
    return <PageContentLoading />;
  }

  return (
    <>
      <PageLayout maxWidth={896}>
        <PageHeaderWithGardenSelector
          title={text.title}
          description={text.description}
          gardens={props.gardens}
          selectedGardenId={props.selectedGardenId}
          onGardenChange={(id) => props.onGardenChange(id as Id<'gardens'> | null)}
          gardenSelectLabel={text.gardenSelectLabel}
        />

        {props.selectedGarden && (
          <AppCard className="mt-4 border-green-300 bg-green-50 p-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-green-800">{text.quickAddTitle}</span>
                <p className="text-sm text-green-700">{text.quickAddHint}</p>
              </div>

              {props.gardenPlants.length === 0 ? (
                <p className="text-muted-foreground text-sm">{text.emptyPlantsInGarden}</p>
              ) : (
                <form onSubmit={props.onQuickAddSubmit}>
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2">
                      <Label>{text.plantFieldLabel}</Label>
                      <SimpleSelect
                        value={props.quickAddPlantId}
                        onValueChange={props.onQuickAddPlantChange}
                        options={plantOptions}
                        placeholder={text.plantFieldPlaceholder}
                        disabled={props.isSaving}
                      />
                    </div>

                    <div className="flex flex-wrap items-end gap-2">
                      <div className="flex min-w-[120px] flex-1 flex-col gap-2">
                        <Label>{text.amountFieldLabel}</Label>
                        <Input
                          inputMode="numeric"
                          value={props.quickAddAmount}
                          onChange={(event) => props.onQuickAddAmountChange(event.target.value)}
                          placeholder={
                            props.quickAddMeasureUnit === 'count'
                              ? text.amountQuantityPlaceholder
                              : text.amountWeightPlaceholder
                          }
                          disabled={props.isSaving}
                        />
                      </div>
                      <MeasureUnitToggle
                        value={props.quickAddMeasureUnit}
                        onChange={props.onQuickAddMeasureUnitChange}
                        disabled={props.isSaving}
                      />
                      <Button type="submit" disabled={props.isSaving || !canQuickAdd}>
                        {text.logHarvest}
                      </Button>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-green-700"
                        onClick={props.onToggleQuickAddShowDate}
                        disabled={props.isSaving}>
                        {props.quickAddShowDate ? text.useToday : text.changeDate}
                      </Button>
                      {props.quickAddShowDate && (
                        <Input
                          type="date"
                          className="max-w-[200px] flex-1"
                          value={props.quickAddDate}
                          onChange={(event) => props.onQuickAddDateChange(event.target.value)}
                          disabled={props.isSaving}
                        />
                      )}
                    </div>
                  </div>
                </form>
              )}
            </div>
          </AppCard>
        )}

        <div className="mt-4">
          <AppCard className="p-4">
            <span className="mb-4 block font-semibold">{text.title}</span>

            {props.gardens.length === 0 ? (
              <p className="text-muted-foreground">{text.noGardens}</p>
            ) : !props.selectedGarden ? (
              <p className="text-muted-foreground">{text.noGardenSelected}</p>
            ) : (
              <div className="flex max-h-[700px] flex-col gap-4 overflow-x-hidden overflow-y-auto">
                {props.daySchedule.map((day) => (
                  <DaySection
                    key={day.date}
                    dayName={day.dayName}
                    dateLabel={formatDayHeader(day.date)}
                    isToday={day.date === today}
                    entries={day.entries}
                    isSaving={props.isSaving}
                    onStartEdit={props.onStartEdit}
                    onConfirmDelete={props.onConfirmDelete}
                  />
                ))}
              </div>
            )}
          </AppCard>
        </div>
      </PageLayout>

      <Dialog open={props.editDialogOpen} onOpenChange={(open) => !open && props.onEditCancel()}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{text.editHarvest}</DialogTitle>
          </DialogHeader>
          <form onSubmit={props.onEditSubmit}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2">
                <Label>{text.plantFieldLabel}</Label>
                <SimpleSelect
                  value={props.editPlantId}
                  onValueChange={props.onEditPlantChange}
                  options={plantOptions}
                  placeholder={text.plantFieldPlaceholder}
                  disabled={props.isSaving}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>{text.dateFieldLabel}</Label>
                <Input
                  type="date"
                  value={props.editHarvestDate}
                  onChange={(event) => props.onEditHarvestDateChange(event.target.value)}
                  disabled={props.isSaving}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>{text.amountFieldLabel}</Label>
                <Input
                  inputMode="numeric"
                  value={props.editAmount}
                  onChange={(event) => props.onEditAmountChange(event.target.value)}
                  placeholder={
                    props.editMeasureUnit === 'count'
                      ? text.amountQuantityPlaceholder
                      : text.amountWeightPlaceholder
                  }
                  disabled={props.isSaving}
                />
              </div>

              <MeasureUnitToggle
                value={props.editMeasureUnit}
                onChange={props.onEditMeasureUnitChange}
                disabled={props.isSaving}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={props.onEditCancel}
                disabled={props.isSaving}>
                {sharedText.cancel}
              </Button>
              <Button
                type="submit"
                disabled={props.isSaving || !props.editPlantId || !props.editAmount.trim()}>
                {text.saveHarvest}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={props.deleteDialogOpen}
        onOpenChange={(open) => !open && props.onDeleteCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{text.deleteConfirmTitle}</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">{text.deleteConfirmBody}</p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={props.onDeleteCancel}
              disabled={props.isSaving}>
              {sharedText.cancel}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={props.onDeleteConfirm}
              disabled={props.isSaving}>
              {sharedText.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MeasureUnitToggle({
  value,
  onChange,
  disabled,
}: {
  value: MeasureUnit;
  onChange: (unit: MeasureUnit) => void;
  disabled?: boolean;
}) {
  const units: { id: MeasureUnit; label: string }[] = [
    { id: 'count', label: text.measureQuantity },
    { id: 'g', label: text.weightUnitG },
    { id: 'kg', label: text.weightUnitKg },
  ];

  return (
    <div className="border-border flex shrink-0 overflow-hidden rounded-lg border">
      {units.map((unit) => (
        <Button
          key={unit.id}
          type="button"
          variant={value === unit.id ? 'default' : 'ghost'}
          size="sm"
          className="rounded-none"
          onClick={() => onChange(unit.id)}
          disabled={disabled}>
          {unit.label}
        </Button>
      ))}
    </div>
  );
}

interface DaySectionProps {
  dayName: string;
  dateLabel: string;
  isToday: boolean;
  entries: HarvestProps['daySchedule'][number]['entries'];
  isSaving: boolean;
  onStartEdit: HarvestProps['onStartEdit'];
  onConfirmDelete: HarvestProps['onConfirmDelete'];
}

function DaySection({
  dayName,
  dateLabel,
  isToday,
  entries,
  isSaving,
  onStartEdit,
  onConfirmDelete,
}: DaySectionProps) {
  return (
    <div className="flex flex-col">
      <div
        className={cn(
          'mb-2 flex items-center gap-2',
          isToday ? 'text-yellow-700' : 'text-muted-foreground'
        )}>
        <div
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold',
            isToday ? 'bg-yellow-500 text-white' : 'bg-muted text-muted-foreground'
          )}>
          {dateLabel.split(' ')[0]}
        </div>
        <span className={cn('text-sm font-semibold', isToday ? 'text-yellow-700' : '')}>
          {dayName} · {dateLabel}
        </span>
        {isToday && (
          <span className="rounded-md bg-yellow-100 px-1.5 py-0.5 text-xs text-yellow-800">
            {text.today}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2 pl-6">
        {entries.length === 0 ? (
          <p className="text-muted-foreground text-sm">{text.noHarvestsDay}</p>
        ) : (
          entries.map((entry) => (
            <div
              key={entry.harvestId}
              className="border-border bg-background flex items-center justify-between rounded-lg border p-2">
              <div className="flex flex-1 items-center gap-2">
                <span className="text-xl">{entry.plantEmoji}</span>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">{entry.plantName}</span>
                  <span className="text-muted-foreground text-sm">
                    {entry.measureType === 'quantity'
                      ? `${entry.amountLabel} ${text.measureQuantity.toLowerCase()}`
                      : entry.amountLabel}
                  </span>
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onStartEdit(entry.harvestId as Id<'harvests'>)}
                  disabled={isSaving}
                  aria-label={text.editAction}>
                  <FaIcon icon={faPenToSquare} className="size-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onConfirmDelete(entry.harvestId as Id<'harvests'>)}
                  disabled={isSaving}
                  aria-label={text.deleteAction}>
                  <FaIcon icon={faTrash} className="size-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
